import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ParticipantWithAmount, useAddExpense } from '../hooks/mutations/useAddExpense';
import { useModal } from '../hooks/useModal';
import { CurrencyCode } from '../constants/currencies';
import { formatCurrency } from '../utils/formatters';
import TransferConfirmModal from '../modals/TransferConfirmModal';  
import TransferModal from '../modals/TransferModal';
import { toast } from 'react-hot-toast';
import { Member } from '../types/member';
import { josa } from 'es-hangul';
import { Banknote, HandCoins, UserPlus2 } from 'lucide-react';
import { useProfile } from '../hooks/queries/useMe';
import RegisterBankModal from '../modals/RegisterBankModal';

interface MyBalanceItemProps {
  members: Member[];
  myMemberId: string;
  memberNameMap: Map<string, string>;
  to: string;
  from: string;
  amount: number;
  currency: CurrencyCode;
  openInviteModal: () => void;
}

const MyBalanceItem: React.FC<MyBalanceItemProps> = ({ 
  members,
  myMemberId,
  memberNameMap,
  to,
  from,
  amount,
  currency,
  openInviteModal,
}) => {
  const { id: groupId } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const { mutateAsync: addExpense } = useAddExpense();
  const { showModal, closeModal } = useModal();
  const {data: userProfile} = useProfile();

  const canShare = navigator.share !== undefined;

  const isToMe = to===myMemberId;
  const shouldInvite = isToMe ? !members.find(member => member.id === from)?.user_id: !members.find(member => member.id === to)?.user_id;
  const shouldRegisterBank = isToMe && !userProfile?.kakaopay_id && !userProfile?.bank;

  const toName = memberNameMap.get(to);
  const fromName = memberNameMap.get(from);

  const getColor = () => {
    if (isToMe) return 'text-blue-500'
    return 'text-red-500';
  };

  const getText = () => {
    if (shouldRegisterBank) return '송금받기';
    if (shouldInvite) return '초대하기';
    if (isToMe) return '재촉하기';
    return '송금하기';
  }

  const getIcon = () => {
    if (shouldRegisterBank) return <HandCoins className='w-5 h-5' />;
    if (shouldInvite) return <UserPlus2 className='w-5 h-5' />;
    if (isToMe) return <HandCoins className='w-5 h-5' />;
    return <Banknote className='w-5 h-5' />;
  }

  const handleConfirmTransfer = async () => {
    if (!groupId || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    try {
      setIsSubmitting(true);
      
      // 송금 기록 생성
      // to가 내 ID라면 다른 사람이 나에게 보낸 것이므로, paidBy는 from이 되고 receiver는 to(나)가 됨
      // from이 내 ID라면 내가 다른 사람에게 보낸 것이므로, paidBy는 from(나)가 되고 receiver는 to가 됨
      const paidBy = to === myMemberId ? from : from === myMemberId ? from : '';
      const receiverId = to === myMemberId ? to : from === myMemberId ? to : '';
      
      if (!paidBy || !receiverId) {
        console.error('송금 정보 오류');
        return;
      }

      // 송금 정보 등록
      const receiverSet: ParticipantWithAmount[] = [{
        id: receiverId,
        amount: Math.abs(amount),
        amountInCurrency:Math.abs(amount)
      }];
      
      await addExpense({
        groupId,
        title: '개인송금',
        amount: Math.abs(amount),
        amountInCurrency:Math.abs(amount),
        spentAt: new Date(new Date().toLocaleDateString('ko-kr', {year:'numeric', month:'2-digit', day:'2-digit'}).replace(/\./g,'').replace(/ /g,'').replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')),
        currency,
        exchangeRate: 1,
        paidBy,
        participants: receiverSet,
        isTransfer: true,
        transferTo: receiverId,
      });
      
      // 모달 닫기
      closeModal();
    } catch (error: any) {
      toast.error(`오류: ${error.message}`);
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  const openTransferModal = () => {
    showModal(
      <TransferModal 
        toId={to}
        toName={toName}
        amount={amount}
        currency={currency}
        openConfirmModal={openTransferConfirmModal}
      />
    );
  };  

  const openTransferConfirmModal = (channel: string | null) => {
    showModal(
      <TransferConfirmModal 
        toName={toName}
        fromName={fromName}
        channel={channel}
        isToMe={to === myMemberId}
        onConfirm={handleConfirmTransfer}
        closeModal={closeModal}
        isSubmitting={isSubmitting}
      />
    );
  };

  const openRegisterBankModal = () => {
    showModal(
      <RegisterBankModal handleOpenTransferConfirmModal={openTransferConfirmModal} />
    );
  };

  const handleHurryUp = async () => {
    const url = window.location.href; // ?activeTab=settlement
    if (canShare) {
      navigator.share({
        title: '정산금을 보내주세요!',
        text: `${fromName}님, ${toName}님께 보내야 할 정산금이 있어요!`,
        url: url
      })
      .then(() => closeModal())
      .catch(err => console.error('공유 실패:', err));
    } else {
      toast.error('공유 기능을 지원하는 브라우저가 아니에요.');
    }
  }

  return (
    <div className="w-full bg-gray-50 border border-gray-100 rounded-lg p-4 flex flex-col items-center justify-center gap-2">
      <div className='self-start text-lg font-bold text-gray-800'>
        {isToMe ? fromName : toName}{isToMe ? josa.pick(fromName,'으로부터/로부터') : '에게'} <span className={`${getColor()}`}>{formatCurrency(Math.abs(amount), currency)}</span> {isToMe ? '받기' : '보내기'}
      </div>
      {
        shouldInvite && !shouldRegisterBank ? <span className='self-start text-gray-500 -mt-1'>아직 그룹에 들어오지 않았어요!</span> : null
      }
      <div className='w-full flex gap-2'>
        <div 
          className={`flex-1 flex items-center justify-center gap-2 py-1.5 bg-white rounded-lg text-center text-lg font-bold text-gray-700 cursor-pointer`}
          onClick={() => openTransferConfirmModal(null)}
        >
          정산완료
        </div>
        {/* {isToMe && <div 
          className={`flex-1 flex items-center justify-center gap-2 py-1.5 bg-white rounded-lg text-center text-lg font-bold text-gray-700 cursor-pointer`}
          onClick={() => openTransferConfirmModal(null)}
        >
          정산완료
        </div>} */}
        {
          getText() === '재촉하기' ? null : (
          <div 
          className={`flex-1 flex items-center justify-center gap-1 py-1.5 bg-white rounded-lg text-center text-lg font-bold ${getColor()} cursor-pointer`}
          onClick={shouldRegisterBank ? openRegisterBankModal : shouldInvite ? () => openInviteModal() : isToMe ? () => handleHurryUp() : openTransferModal}
        >
          {getIcon()}
          {getText()}
        </div>)
        }
      </div>
    </div>
  )
};

export default MyBalanceItem; 